// W05 / M05 — Feladatlista.
//
// Desktopon sűrű táblázat oszlopfejléccel, mobilon kártyák. Ugyanaz az adat,
// két sűrűségben; a váltást CSS végzi, nem JavaScript.

import { avatarStack, counters, deadlineLabel, emptyState, priorityTag, skeletonList, statusBadge } from '../lib/components';
import { t } from '../lib/i18n';
import { h, icon, icons } from '../lib/dom';
import type { PersonView, ProjectView, TaskTab } from '../lib/data';
import { filterTasks } from '../lib/data';
import { applyFilter, emptyFilter, type TaskFilter } from './filters';
import { priorityLabel, statusLabel, type TaskView } from '../lib/model';

export type TasksPageOptions = {
  tab: TaskTab;
  userId: string;
  onTaskSelected: (task: TaskView) => void;
  onTabChange: (tab: TaskTab) => void;
  onNewTask: () => void;
  selectedTaskId?: string;
  /// Az éppen érvényes szűrő. Hiánya esetén a szűrősáv el sem készül — így a
  /// képernyő önmagában is használható marad.
  filter?: TaskFilter;
  /// A szűrőpanel megnyitása.
  onOpenFilters?: () => void;
  /// Egy chip × gombja, illetve a „Szűrők törlése".
  onFilterChange?: (filter: TaskFilter) => void;
  /// Állapotváltás KÖZVETLENÜL a listasorból, a részletek panel megnyitása
  /// nélkül. A soron eddig semmi nem jelezte, hogy hol lehet státuszt váltani.
  onChangeStatus?: (task: TaskView) => void;
};

/// A fülekhez tartozó, SZŰRETLEN darabszám.
///
/// Szándékosan nem a szűrt lista hossza: a terven „Nyitott 12" és „Összes 48"
/// áll egyszerre, tehát a szám a fület írja le, nem a bekapcsolt chipek
/// eredményét. Ha a chipekkel együtt változna, a fülsor elvesztené a
/// tájékozódási értékét.
function tabCount(
  projects: ProjectView[],
  tab: TaskTab,
  userId: string,
): number {
  return filterTasks(projects, tab, userId).length;
}

/// A fulek feliratainak KULCSAI. Kesz szoveget nem tarolhat modul szintu
/// tabla: a szotar csak a render elott erkezik meg.
const tabLabelKeys: Record<TaskTab, string> = {
  open: 'nav.tasksOpen',
  mine: 'nav.tasksMine',
  previous: 'nav.tasksPrevious',
  all: 'nav.tasksAll',
};

function tabs(
  active: TaskTab,
  onChange: (tab: TaskTab) => void,
  onNewTask: () => void,
  countOf: (tab: TaskTab) => number,
): HTMLElement {
  return h(
    'div',
    {
      class: 'tabs row between',
      role: 'tablist',
      // Keskeny kepernyon a fulek es az „Uj feladat” gomb egy sorban 406 px-et
      // kernek: a gomb 31 px-cel kilogott, es csak oldalra gorgetve volt
      // elerheto. Tordelessel a gomb a fulek ALA kerul, gorgetes nelkul.
      style: { padding: '0 16px', flexWrap: 'wrap', rowGap: '8px' },
    },
    h(
      'span',
      { class: 'row g4' },
    ...(Object.keys(tabLabelKeys) as TaskTab[]).map((tab) =>
      h(
        'button',
        {
          class: `tab${tab === active ? ' on' : ''}`,
          type: 'button',
          role: 'tab',
          'aria-selected': tab === active ? 'true' : 'false',
          onClick: () => onChange(tab),
        },
        t(tabLabelKeys[tab]),
        // A terv minden fül mellé kiírja a darabszámot.
        h('span', { class: 'mono tm', text: String(countOf(tab)) }),
      ),
    ),
    ),
    h('button', {
      class: 'btn btn-primary btn-sm',
      type: 'button',
      text: t('task.newTask'),
      onClick: onNewTask,
    }),
  );
}

/// A terv szűrősávja a fülsor alatt: „Szűrők N", az aktív szűrők chipjei, és
/// jobbra a törlés.
///
/// A szűrőpanel (`filters.ts`) eddig MEGVOLT, de sehonnan nem nyílt: a webes
/// feladatlistán egyáltalán nem lehetett szűrni.
function filterBar(
  options: TasksPageOptions,
  state: { projects: ProjectView[]; people: PersonView[] },
): HTMLElement | null {
  const { filter, onOpenFilters, onFilterChange } = options;
  if (!filter || !onOpenFilters || !onFilterChange) return null;

  const chips: HTMLElement[] = [];
  const emit = () => onFilterChange(filter);

  function chip(label: string, remove: () => void): HTMLElement {
    return h(
      'span',
      { class: 'chip on' },
      label,
      h('button', {
        class: 'x',
        type: 'button',
        'aria-label': t('filter.removeChip', [label]),
        text: '×',
        style: { border: '0', background: 'none', cursor: 'pointer', font: 'inherit' },
        onClick: () => {
          remove();
          emit();
        },
      }),
    );
  }

  for (const status of filter.statuses) {
    chips.push(
      chip(t('task.statusChip', [statusLabel(status)]), () => filter.statuses.delete(status)),
    );
  }
  for (const priority of filter.priorities) {
    chips.push(
      chip(t('task.priorityChip', [priorityLabel(priority)]), () =>
        filter.priorities.delete(priority),
      ),
    );
  }
  if (filter.deadline !== 'all') {
    const labels: Record<string, string> = {
      today: 'Ma',
      thisWeek: t('filter.thisWeek'),
      overdue: t('filter.overdue'),
      custom: t('filter.custom'),
    };
    chips.push(
      chip(t('task.deadlineChip', [labels[filter.deadline] ?? filter.deadline]), () => {
        filter.deadline = 'all';
        filter.from = undefined;
        filter.to = undefined;
      }),
    );
  }
  for (const id of filter.assigneeIds) {
    const name = state.people.find((p) => p.id === id)?.fullName ?? id;
    chips.push(chip(t('task.assigneeChip', [name]), () => filter.assigneeIds.delete(id)));
  }
  for (const id of filter.projectIds) {
    const title = state.projects.find((p) => p.id === id)?.title ?? id;
    chips.push(chip(`Projekt: ${title}`, () => filter.projectIds.delete(id)));
  }
  if (filter.onlyFollowed) {
    chips.push(chip(t('task.onlyFollowed'), () => (filter.onlyFollowed = false)));
  }
  if (filter.onlyPending) {
    chips.push(chip(t('task.onlyPending'), () => (filter.onlyPending = false)));
  }

  const activeCount = chips.length;

  return h(
    'div',
    {
      class: 'row g8 wrap',
      style: {
        padding: '10px 16px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        alignItems: 'center',
      },
    },
    h(
      'button',
      { class: 'btn btn-secondary btn-sm', type: 'button', onClick: onOpenFilters },
      h('span', { class: 'ic', style: { width: '14px', height: '14px' } }, icon(icons.filter, 14)),
      t('filter.shortTitle'),
      activeCount > 0 ? h('span', { class: 'mono', text: String(activeCount) }) : null,
    ),
    ...chips,
    activeCount > 0
      ? h('button', {
          class: 'btn btn-ghost btn-sm',
          type: 'button',
          style: { marginLeft: 'auto' },
          text: t('filter.clear'),
          onClick: () => onFilterChange(emptyFilter()),
        })
      : null,
  );
}

/// A terv oszlopsorrendje: azonosító, feladat, prioritás, státusz, határidő,
/// felelős, egyéb. Korábban a státusz és a prioritás fordítva állt, a
/// határidő pedig a felelős UTÁN — a tervben a határidő előrébb van, mert
/// azt olvassa le az ember először.
///
/// A terv első oszlopa egy kijelölő jelölőnégyzet: azt NEM vettük át, mert
/// tömeges műveletek (több feladat egyszerre) nincsenek a rendszerben, a
/// négyzet így semmit nem indítana.
function tableHeader(firstColumnLabel = 'Feladat'): HTMLElement {
  const cell = (label: string, width?: string, right = false) =>
    h('span', {
      class: 'th',
      style: {
        ...(width ? { width, flex: 'none' } : { flex: '1' }),
        ...(right ? { textAlign: 'right' } : {}),
      },
      text: label,
    });

  return h(
    'div',
    { class: 'thead' },
    cell(t('task.colId'), '84px'),
    cell(firstColumnLabel),
    cell(t('task.priority'), '92px'),
    cell(t('task.status'), '152px'),
    cell(t('general.deadline'), '128px'),
    cell(t('task.stepAssignee'), '74px'),
    cell(t('task.colOther'), '92px', true),
  );
}

function nameOf(people: PersonView[], id: string): string {
  return people.find((p) => p.id === id)?.fullName ?? 'Ismeretlen';
}

/// Sűrű desktop sor.
function taskRow(
  task: TaskView,
  people: PersonView[],
  onSelect: (task: TaskView) => void,
  selectedTaskId?: string,
  onChangeStatus?: (task: TaskView) => void,
): HTMLElement {
  const selected = task.id === selectedTaskId;
  return h(
    'button',
    {
      type: 'button',
      class: `lrow${selected ? ' on' : ''}`,
      // A `.lrow` alapbol tordel (a mobilkartyakhoz keszult); a suru
      // tablazatsorban ez a hosszu cimu feladatot ket sorba torte, es az
      // oszlopok elcsusztak.
      style: { width: '100%', border: '0', borderBottom: '1px solid var(--border)', textAlign: 'left', font: 'inherit', cursor: 'pointer', flexWrap: 'nowrap', padding: '11px 16px' },
      'aria-current': selected ? 'true' : undefined,
      onClick: () => onSelect(task),
    },
    // A terven „BW-2026-0418" alakú azonosító áll. A modellben csak
    // `sequenceNumber` van — kitalált előtagot nem írunk ki, így a sorszám
    // marad, és az oszlop is annyi, amennyit az elfér (84 px a terv 108
    // helyett).
    h('span', {
      class: 'mono caption',
      style: { width: '84px', flex: 'none' },
      text: task.sequenceNumber ? `#${task.sequenceNumber}` : '—',
    }),
    h(
      'span',
      { class: 'col grow', style: { gap: '2px', minWidth: '0' } },
      h('span', {
        class: 'body-sm w6',
        style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        text: task.title,
      }),
      task.projectTitle
        ? h('span', {
            class: 'caption',
            style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            text: task.projectTitle,
          })
        : null,
    ),
    h(
      'span',
      { style: { width: '92px', flex: 'none' } },
      task.priority ? priorityTag(task.priority) : h('span', { class: 'caption tm', text: '—' }),
    ),
    h(
      'span',
      { style: { width: '152px', flex: 'none' } },
      // A statusz-jelvény KATTINTHATO, ha van mit tenni vele.
      //
      // A soron eddig semmi nem arulta el, hogy megnyitja a reszletek panelt,
      // es hogy az allapotvaltas ott van. A leggyakoribb muvelet igy ket
      // felfedezetlen lepes mogott allt.
      //
      // `role="button"` es NEM `<button>`: maga a SOR egy gomb, gombot pedig
      // nem lehet gombba agyazni. Billentyuvel ugyanugy mukodik, es a
      // kattintas nem terjed tovabb a sorra — kulonben egyszerre nyilna a
      // panel es a parbeszed.
      onChangeStatus
        ? (() => {
            const cell = statusBadge(task.status);
            cell.setAttribute('role', 'button');
            cell.setAttribute('tabindex', '0');
            cell.setAttribute('title', t('task.changeStatusShort'));
            cell.setAttribute('aria-label', t('task.changeStatusShort'));
            cell.style.cursor = 'pointer';
            cell.addEventListener('click', (event) => {
              event.stopPropagation();
              onChangeStatus(task);
            });
            cell.addEventListener('keydown', (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              onChangeStatus(task);
            });
            return cell;
          })()
        : statusBadge(task.status),
    ),
    h(
      'span',
      { style: { width: '128px', flex: 'none' } },
      deadlineLabel(task, true) ?? h('span', { class: 'caption tm', text: '—' }),
    ),
    h(
      'span',
      { style: { width: '74px', flex: 'none', display: 'flex', justifyContent: 'flex-end' } },
      task.assigneeIds.length > 0
        ? avatarStack(task.assigneeIds.map((id) => nameOf(people, id)), 2)
        : h('span', { class: 'caption tm', text: '—' }),
    ),
    h('span', { style: { width: '92px', flex: 'none' } }, counters(task, true)),
  );
}

/// A terv sűrű feladattáblája, panelbe zárva.
///
/// A feladatlista (W05) és a projekt részletei (W20) UGYANAZT a táblát
/// mutatják — közös függvény, hogy az oszlopok ne csússzanak szét a két
/// képernyő között.
export function denseTaskTable(options: {
  tasks: TaskView[];
  people: PersonView[];
  onSelect: (task: TaskView) => void;
  selectedTaskId?: string;
  firstColumnLabel?: string;
  /// Ha meg van adva, a statusz-jelvény kattinthatóvá válik a sorban.
  onChangeStatus?: (task: TaskView) => void;
}): HTMLElement {
  return h(
    'div',
    { class: 'panel dense', style: { overflow: 'hidden' } },
    tableHeader(options.firstColumnLabel),
    ...options.tasks.map((task) =>
      taskRow(
        task,
        options.people,
        options.onSelect,
        options.selectedTaskId,
        options.onChangeStatus,
      ),
    ),
  );
}

/// Mobil kártya.
function taskCard(
  task: TaskView,
  people: PersonView[],
  options: TasksPageOptions,
): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      class: 'card pad16 col g12',
      style: { width: '100%', textAlign: 'left', font: 'inherit', cursor: 'pointer' },
      onClick: () => options.onTaskSelected(task),
    },
    h(
      'span',
      { class: 'row g6 wrap' },
      statusBadge(task.status),
      task.priority ? priorityTag(task.priority) : null,
    ),
    h(
      'span',
      { class: 'col', style: { gap: '2px' } },
      h(
        'span',
        { class: 'row g6' },
        task.sequenceNumber
          ? h('span', { class: 'mono tm', text: `${task.sequenceNumber}.` })
          : null,
        h('span', { class: 'body w6', text: task.title }),
      ),
      task.projectTitle ? h('span', { class: 'caption', text: task.projectTitle }) : null,
    ),
    h(
      'span',
      { class: 'row between' },
      task.assigneeIds.length > 0
        ? avatarStack(task.assigneeIds.map((id) => nameOf(people, id)), 3)
        : h('span', { text: '' }),
      deadlineLabel(task) ?? h('span', { text: '' }),
    ),
    counters(task),
  );
}

export function tasksPage(
  state: { loading: boolean; projects: ProjectView[]; people: PersonView[]; error?: string },
  options: TasksPageOptions,
): HTMLElement {
  const header = tabs(options.tab, options.onTabChange, options.onNewTask, (tab) =>
    tabCount(state.projects, tab, options.userId),
  );
  const bar = filterBar(options, state);

  if (state.loading) {
    return h('div', { class: 'col' }, header, skeletonList());
  }

  if (state.error) {
    return h(
      'div',
      { class: 'col' },
      header,
      h(
        'div',
        { style: { padding: '16px' } },
        h(
          'div',
          { class: 'toast bad' },
          h(
            'span',
            { class: 'col', style: { gap: '2px' } },
            h('span', { class: 'body-sm w6', text: t('task.loadFailed') }),
            h('span', { class: 'caption', text: state.error }),
          ),
        ),
      ),
    );
  }

  const byTab = filterTasks(state.projects, options.tab, options.userId);
  const tasks = options.filter
    ? applyFilter(byTab, options.filter, { userId: options.userId })
    : byTab;

  if (tasks.length === 0) {
    return h(
      'div',
      { class: 'col' },
      header,
      bar,
      emptyState({
        title: 'Nincsenek feladatok',
        text:
          options.tab === 'open'
            ? t('task.noOpenForYou')
            : t('task.noneOnThisFilter'),
      }),
    );
  }

  return h(
    'div',
    { class: 'col', style: { gap: '0' } },
    header,
    bar,
    // A terv a táblázatot 16 px-es margóval, `.panel dense` keretben mutatja —
    // eddig a sorok a képernyő széléig értek, keret nélkül.
    h(
      'div',
      {
        class: 'hide-mobile',
        style: {
          flexGrow: '1',
          minHeight: '0',
          overflow: 'auto',
          background: 'var(--bg)',
          padding: '16px',
        },
      },
      denseTaskTable({
        tasks,
        people: state.people,
        onSelect: options.onTaskSelected,
        selectedTaskId: options.selectedTaskId,
        onChangeStatus: options.onChangeStatus,
      }),
    ),
    h(
      'div',
      { class: 'task-cards col g12', style: { padding: '16px' } },
      ...tasks.map((task) => taskCard(task, state.people, options)),
    ),
  );
}
